<script lang="ts" setup>
import { ref, computed } from 'vue';

defineOptions({
    name: 'DropZone'
});

const props = withDefaults(defineProps<{
    modelValue: File[];
    enabled?: boolean;
    show?: boolean;
    dark?: boolean;
}>(), {
    enabled: true
});

const emit = defineEmits<{
    (e: 'click', event: MouseEvent): void;
    (e: 'update:modelValue', payload: File[]): void;
}>();

const dragging = ref(0);

const visible = computed((): boolean => props.show || dragging.value > 0);

const onDragEnter = (): void =>
{
    if (!props.enabled)
        return;

    dragging.value++;
};

const onDragLeave = (): void =>
{
    if (!props.enabled)
        return;

    dragging.value++;
};

const returnValue = (event: DragEvent): void =>
{
    const result: File[] = [];

    props.modelValue.forEach(p => result.push(p));

    for (let i = 0; i < event.dataTransfer.files.length; i++)
    {
        result.push(event.dataTransfer.files.item(i));
    }

    emit('update:modelValue', result);
};

const onDrop = (event: DragEvent): void =>
{
    if (props.enabled)
    {
        event.preventDefault();

        returnValue(event);
        dragging.value = 0;
    }
};

const onClick = (e: MouseEvent): void =>
{
    emit('click', e);
};
</script>

<template>
    <div
        class="dropzone flex-fill"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave"
        @dragover.prevent
        @drop.prevent="onDrop"
        @click.prevent="onClick"
    >
        <slot />
        <div v-if="visible" class="zone" :class="{ 'white': !dark, 'dark': dark }" />
    </div>
</template>

<style lang="scss" scoped>
.dropzone {
    position: relative;
    min-height: 150px;
    cursor: pointer;

    .zone {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        color: var(--bs-border-color);
        z-index: 3;

        &::before {
            font-family: var(--fa-font-family);
            font-weight: 600;
            line-height: 1;
            font-size: 5em;
            content: "\f382";
        }
    }

    .white {
        background: var(--bs-secondary-bg);
        border: 1px solid var(--bs-border-color);
    }

    .dark {
        background: var(--bs-dark);
        border: 1px solid var(--bs-border-color);
    }
}
</style>
